import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Contact, HistoriqueLogs } from '../../../../../common/src/entities';
import { schemaContact } from '../../../../../common/src/schemas';
import ChampFormulaire from '../../Common/ChampFormulaire';
import FormulaireModificationEntite from '../Utils/FormulaireModificationEntite';
import { Button, Card, Col, Dropdown, Form, Modal, Row } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import api, { useGetQuery } from '../../../Utils/api';
import { useAlertDialog } from '../../../Utils/dialogContext';
import { useRecoilState } from 'recoil';
import { etatModalAvisAbonnement } from '../../../State';
import { DateTime } from 'luxon';
import ModalAvisAbonnement from '../Utils/ModalAvisAbonnement';
import { LinkContainer } from 'react-router-bootstrap';
import { BiLink } from 'react-icons/all';
import ChampsAssocies from '../Utils/ChampsAssociés';
import Alert from 'react-bootstrap/Alert';
import LogsLoader, { dateFormatter } from '../Utils/Log';
import ClientContact from '../../../../../common/src/entities/client-contact.entity';
import { TitreCarte } from '../../Common/TitreCarte';
import CheckboxFormulaireEtatEntite from '../../Common/CheckboxFormulaireEtatEntite';
import { cssTexte, LABEL_COL } from '../constantes';
import { EtatEntite } from '../../Common/EtatEntite';
import { MyPaper } from '../Utils/MyPaper';

interface IChampsContactState {
    courriel: {
        value?: string | undefined;
        prevValue?: string | undefined;
    };
}

type AlertContentState = {
    theme: string;
    contacts: Contact[];
};

export function ChampsContacts(): ReactElement {
    const {
        values: { fournisseurs_defaut, fournisseurs_gestionnaire, activites, activites_defaut, email },
    } = useFormikContext<Contact>();
    const bsAlert = useAlertDialog();
    const [showAlert, setShowAlert] = useState(false);
    const [alertContent, setAlertContent] = useState<AlertContentState>({ theme: '', contacts: [] });
    const [isEmailDuplicated, setEmailDuplicated] = useState(false);
    const navigate = useNavigate();

    /**
     * Créé une version "debounced" de la fonction passée en argument. Cette version
     * retardée ne sera exécutée que après un certain délai après le dernier appel.
     * Ceci est particulièrement utile pour éviter des appels de fonction trop fréquents
     * comme des requêtes AJAX lors de la frappe de l'utilisateur dans un champ de recherche.
     *
     * @param fn {Function} - La fonction que vous souhaitez "debouncer".
     * @param delay {number} - Le délai en millisecondes pendant lequel la fonction sera retardée.
     * @return {Function} - Retourne la version "debounced" de la fonction.
     *
     * @example
     *
     * const log = debounce((message) => console.log(message), 300);
     * log('Hello');    // ne s'affiche pas immédiatement
     * log('World');    // ne s'affiche pas immédiatement
     * // Après 300ms, "World" est affiché dans la console.
     */
    function debounce(fn: any, delay: number) {
        let timerId: NodeJS.Timeout | null = null;
        return function (...args: any[]) {
            if (timerId) {
                clearTimeout(timerId);
            }
            timerId = setTimeout(() => {
                fn(...args);
                timerId = null;
            }, delay);
        };
    }

    useEffect(() => {
        verifierEmail(email);
    }, [email]);

    const verifierEmailAvecDelai = debounce(verifierEmail, 500);

    async function verifierEmail(email: string) {
        try {
            const reponse = await api.post('/contacts/existe', { email });
            if (reponse.data.exists) {
                setAlertContent({
                    theme: 'warning',
                    contacts: reponse.data.contacts,
                });
                setEmailDuplicated(true); // Set invalid state
                setShowAlert(true);
            } else {
                setEmailDuplicated(false); // Reset invalid state
                setShowAlert(false);
            }
        } catch (erreur) {
            setEmailDuplicated(false);
            setShowAlert(false);
        }
    }

    const [state, setState] = useState<IChampsContactState>({
        courriel: {
            value: email,
        },
    } as IChampsContactState);

    useEffect(() => {
        setState({
            courriel: {
                value: email,
            },
        } as IChampsContactState);
        verifierEmailAvecDelai;
    }, [email]);

    const courrielFormulaireNotifyChangeHandler = (nouveauCourriel: any) => {
        setState(
            (prev) =>
                ({
                    ...prev,
                    courriel: {
                        ...prev.courriel,
                        prevValue: prev.courriel.value,
                        value: nouveauCourriel ? (nouveauCourriel as string) : undefined,
                    },
                } as IChampsContactState),
        );

        // Debounce the email check to avoid making too many server requests while typing
        verifierEmailAvecDelai(nouveauCourriel);
    };
    function hasUpperCase(str: string) {
        return /[A-Z]/.test(str);
    }

    const courrielFormulaireValidateOnChangeHandler = (newCourriel?: string) => {
        // on cherche a verifier le courriel s<il contient une lettre majuscule
        let isNotContainingUpperCase = true;
        if (newCourriel && hasUpperCase(newCourriel)) {
            isNotContainingUpperCase = false;
            if (state.courriel.prevValue && !hasUpperCase(state.courriel.prevValue)) {
                bsAlert("HEY! Y'a une majuscule!", 'warning');
            }
        }
        return isNotContainingUpperCase;
    };

    function obtenirNom(prefixe: string, valeur: string): ReactElement {
        return (
            <div style={cssTexte}>
                {prefixe} <b>{valeur}</b>
            </div>
        );
    }

    const fournisseurs = [
        ...(fournisseurs_defaut ?? []).map((f) => ({
            ...f,
            name: obtenirNom('Contact par défaut de', f.name),
        })),
        ...(fournisseurs_gestionnaire ?? []).map((f) => ({
            ...f,
            name: obtenirNom('Gestionnaire de', f.name),
        })),
    ];

    const activites_combines = [
        ...(activites_defaut ?? []).map((a) => ({
            ...a,
            name: obtenirNom('Contact par défaut de', a.name),
        })),
        ...(activites ?? []).map((a) => ({
            ...a,
            name: obtenirNom('Associé à', a.name),
        })),
    ];

    return (
        <>
            {showAlert && (
                <Alert variant={alertContent.theme} onClose={() => setShowAlert(false)} dismissible>
                    {`Le courriel ${email} est déjà associé à un autre contact. Veuillez utiliser une adresse e-mail différente. `}
                    {alertContent.contacts.map((contact, index) => (
                        <React.Fragment key={contact.id}>
                            <Button variant="link" onClick={() => navigate(`/dashboard/contacts/${contact.id}`)}>
                                {contact.nom}
                            </Button>
                            {index !== alertContent.contacts.length - 1 && ', '}
                        </React.Fragment>
                    ))}
                </Alert>
            )}

            <EtatEntite>
                <CheckboxFormulaireEtatEntite id="actif" titre="Actif" />
                <CheckboxFormulaireEtatEntite id="isAdmin" titre="Activer les droits administrateur" />
            </EtatEntite>
            <MyPaper>
                <Row>
                    <Col xs={LABEL_COL}>Client</Col>
                    <Col xs={2}>
                        <ChampFormulaire
                            id="civilite"
                            suggestions={['Madame', 'Monsieur']}
                            cacherTitre
                            isCreatable
                            placeholder="Civilité"
                            estPetit
                        />
                    </Col>
                    <Col>
                        <ChampFormulaire id="prenom" titre="Prénom" cacherTitre estPetit placeholder="Prénom" />
                    </Col>
                    <Col>
                        <ChampFormulaire id="nom" titre="Nom" cacherTitre estPetit placeholder="Nom" />
                    </Col>
                </Row>
                <ChampFormulaire
                    id="email"
                    titre="Courriel"
                    labelCol={LABEL_COL}
                    estPetit
                    notifyChange={courrielFormulaireNotifyChangeHandler}
                    validateOnChangeCallback={courrielFormulaireValidateOnChangeHandler}
                    emailUnique={isEmailDuplicated}
                    errorMessage={isEmailDuplicated ? 'Le courriel saisi est déjà associé à un autre contact.' : ''}
                />

                <ChampFormulaire id="telephone" titre="Numéro de téléphone" labelCol={LABEL_COL} estPetit />
                <ChampFormulaire id="notes" titre="Notes internes" textarea labelCol={LABEL_COL} />
            </MyPaper>
            <TitreCarte>Associés</TitreCarte>
            <MyPaper>
                <ChampsAssocies items={fournisseurs} url="fournisseurs" titre="Fournisseurs" labelCol={LABEL_COL} />
                <ChampsAssocies items={activites_combines} url="activites" titre="Activités" labelCol={LABEL_COL} />
                <ChampsClientsAssocies labelCol={LABEL_COL} />
            </MyPaper>
        </>
    );
}

export interface BaseClientEntite {
    id?: number;
    name: string;
    created_at: string;
    fin_abonnement: string;
    actif?: boolean;
}
export interface PropsChampsClientsAssocies {
    labelCol: number;
    avecUnBouton?: boolean;
}

export function ChampsClientsAssocies(props: PropsChampsClientsAssocies): ReactElement {
    const [isToggled, setIsToggled] = useState(false);
    const handleToggle = () => {
        setIsToggled(!isToggled);
    };

    const textButton = !isToggled ? 'Voir le(s) client(s) inactif(s)' : 'Voir uniquement le(s) client(s) actif(s)';

    const {
        values: { clients },
    } = useFormikContext<Contact>();
    function clientEstActif(dateFinAbonnement: string): string {
        return new Date() < new Date(dateFinAbonnement) ? 'Actif' : 'Inactif';
    }

    function isAllActif(values: ClientContact[]): boolean {
        let allActif = true;
        if (values) {
            values.forEach((item: ClientContact) => {
                if (!item.actif) {
                    allActif = false;
                }
            });
        }
        return allActif;
    }

    const allActif = isAllActif(clients);

    const cssCardBody: React.CSSProperties = {
        padding: 0,
        minHeight: 30,
    };

    return (
        <Form.Group>
            <Row>
                <Col xs={props.labelCol}>Clients</Col>
                <Col>
                    <Card>
                        <Card.Body style={cssCardBody}>
                            {clients?.map(({ client: value, actif }) => {
                                if (!isToggled) {
                                    if (actif !== true) {
                                        return null;
                                    }
                                    return (
                                        <div
                                            className="d-flex"
                                            style={{ color: actif ? 'black' : 'gray' }}
                                            key={value.id}
                                        >
                                            <div className="flex-grow-0">
                                                <LinkContainer to={'../../clients/' + value.id}>
                                                    <Button variant="link" className="btn-sm text-muted">
                                                        <BiLink size="1.5em" />
                                                    </Button>
                                                </LinkContainer>
                                            </div>
                                            <div className="pt-1">
                                                <Card.Title className="d-flex align-items-center">
                                                    {value.name}
                                                </Card.Title>
                                                <Card.Text>
                                                    Abonnement :{' '}
                                                    {value.fin_abonnement != null
                                                        ? clientEstActif(value.fin_abonnement)
                                                        : 'Information non disponible'}
                                                </Card.Text>
                                                <Card.Text>
                                                    Date de fin d&apos;abonnement :{' '}
                                                    {value.fin_abonnement != null
                                                        ? DateTime.fromISO(value.fin_abonnement).toISODate()
                                                        : 'Information non disponible'}
                                                </Card.Text>
                                            </div>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div
                                            className="d-flex"
                                            style={{ color: actif ? 'black' : 'gray' }}
                                            key={value.id}
                                        >
                                            <div className="flex-grow-0">
                                                <LinkContainer to={'../../clients/' + value.id}>
                                                    <Button variant="link" className="btn-sm text-muted">
                                                        <BiLink size="1.5em" />
                                                    </Button>
                                                </LinkContainer>
                                            </div>
                                            <div className="pt-1">
                                                <Card.Title className="d-flex align-items-center">
                                                    {value.name}
                                                </Card.Title>
                                                <Card.Text>
                                                    Abonnement :{' '}
                                                    {value.fin_abonnement != null
                                                        ? clientEstActif(value.fin_abonnement)
                                                        : 'Information non disponible'}
                                                </Card.Text>
                                                <Card.Text>
                                                    Date de fin d&apos;abonnement :{' '}
                                                    {value.fin_abonnement != null
                                                        ? DateTime.fromISO(value.fin_abonnement).toISODate()
                                                        : 'Information non disponible'}
                                                </Card.Text>
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                        </Card.Body>
                    </Card>

                    <button
                        hidden={allActif}
                        type="button"
                        className="btn btn-outline-primary mt-2 btn-sm"
                        onClick={handleToggle}
                    >
                        {textButton}
                    </button>
                </Col>
            </Row>
        </Form.Group>
    );
}

export function ModalAvisAbonnementEx(): ReactElement {
    const {
        values: { clients },
    } = useFormikContext<Contact>();

    const mappedClients: BaseClientEntite[] =
        clients?.map((clientContact) => ({
            id: clientContact.client.id,
            name: clientContact.client.name,
            created_at: clientContact.client.created_at,
            fin_abonnement: clientContact.client.fin_abonnement,
            actif: clientContact.actif,
        })) || [];

    const activeClients = mappedClients.filter((client) => client.actif);

    return (
        <>
            <ModalAvisAbonnement clients={activeClients} />
        </>
    );
}

export default function DetailsContact(): ReactElement {
    const { id } = useParams() as { id: string };
    const listLogs = useGetQuery<HistoriqueLogs[]>(`historique_logs/${id}/contact`);
    const bsAlert = useAlertDialog();
    const [etatAvisAbonnement, setEtatClientsAssocies] = useRecoilState(etatModalAvisAbonnement);
    const [showModal, setShowModal] = useState(false);

    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    async function soumettreProcessusChangementMotDePasse() {
        try {
            const reponse = await api.patch('/contacts/motdepasse/' + id);
            bsAlert(reponse.data.message, 'success');
            handleClose();
        } catch (_) {
            bsAlert("Erreur lors de l'envoie du processus de mot de passe.", 'danger');
            handleClose();
        }
    }

    function afficherModalAvisAbonnement() {
        const clientsAssocies = etatAvisAbonnement.clientsAssocies;
        setEtatClientsAssocies({ contactId: id, clientsAssocies: clientsAssocies, afficher: true });
    }

    const actionsContact = (value: Contact) => (
        <>
            <Dropdown.Item onClick={handleShow}>Envoyer processus changement de mot de passe</Dropdown.Item>
            <Dropdown.Item onClick={afficherModalAvisAbonnement}>Envoyer un avis d&apos;abonnement</Dropdown.Item>
        </>
    );

    return (
        <div>
            <FormulaireModificationEntite
                id={+id}
                entite={Contact}
                badges={(c: Contact) => [
                    `Contact #${c.id}`,
                    `Créé: ${DateTime.fromISO(c.created_at).toRelativeCalendar()}`,
                    `Dernière modification: ${
                        listLogs.data?.[listLogs.data.length - 1]?.date_modification
                            ? dateFormatter(listLogs.data?.[listLogs.data.length - 1]?.date_modification)
                            : 'Aucune modificiation'
                    }  ${
                        listLogs.data?.[listLogs.data.length - 1]?.nom_usager
                            ? ' par ' + listLogs.data?.[listLogs.data.length - 1]?.nom_usager
                            : ''
                    }`,
                ]}
                schema={schemaContact}
                actions={actionsContact}
            >
                <ChampsContacts />
                <ModalAvisAbonnementEx />
            </FormulaireModificationEntite>
            <LogsLoader id={id} tableName={'contact'} />
            <Modal show={showModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>Êtes-vous sûr de vouloir envoyer le processus de changement de mot de passe ?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Annuler
                    </Button>
                    <Button variant="primary" onClick={soumettreProcessusChangementMotDePasse}>
                        Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
